import React, { Fragment, useEffect, useState } from "react";
import { styled } from "@mui/material/styles";
import {
  CircularProgress,
  Dialog,
  DialogTitle,
  Hidden,
  SwipeableDrawer,
  Typography,
  Rating,
} from "@mui/material";
import { StarBorder } from "@mui/icons-material";
import InfiniteScroll from "react-infinite-scroll-component";

import useShopContext from "@hooks/useShopContext";
import { fetchShopTestimonials } from "../api";
import { Stack } from "@mui/system";

const PREFIX = "ShopReviews";

const classes = {
  list: `${PREFIX}-list`,
  dragHandle: `${PREFIX}-dragHandle`,
  reviewSectionHeader: `${PREFIX}-reviewSectionHeader`,
  reviewSectionTitle: `${PREFIX}-reviewSectionTitle`,
  reviewRatingSection: `${PREFIX}-reviewRatingSection`,
  reviewDialog: `${PREFIX}-reviewDialog`,
};

const StyledTypography = styled(Typography)(({ theme, textcolor }) => ({
  color: textcolor || theme.palette.primary.contrastText,
}));

const StyledRating = styled(Rating)(({ theme }) => ({ color: theme.palette.secondary.main }));

const StyledStarBorder = styled(StarBorder)(({ theme }) => ({
  color: theme.palette.secondary.main,
}));

const StyledReview = styled("div")(({ theme }) => ({
  overflowY: "auto",
  padding: theme.spacing(0, 3),
  [theme.breakpoints.down("md")]: {
    padding: theme.spacing(0, 2),
  },
  "&::-webkit-scrollbar": {
    width: 5,
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: theme.palette.secondary.main,
    borderRadius: 5,
  },
  [`& .${classes.reviewRatingSection}`]: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "8px",
    margin: "3px 0px",
  },
}));

const StyledEmptyReview = styled("div")(({ theme }) => ({
  padding: theme.spacing(5),
  flexGrow: 1,
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  gap: theme.spacing(1),
}));

const StyledCircularProgress = styled(CircularProgress)(({ theme }) => ({
  color: theme.palette.secondary.main,
}));

const StyledSwipeableDrawer = styled(SwipeableDrawer)(({ theme }) => ({
  "& .MuiDrawer-paper": {
    borderRadius: "30px 30px 0px 0px",
    height: "80%",
    width: "100%",
    margin: "auto",
    [`& .${classes.list}`]: {
      display: "flex",
      flexDirection: "column",
      height: "100%",
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
    },
    [`& .${classes.reviewSectionHeader}`]: {
      padding: theme.spacing(2),
      position: "sticky",
      zIndex: 100,
    },
    [`& .${classes.dragHandle}`]: {
      width: 35,
      height: 6,
      borderRadius: 3,
      margin: "auto",
      backgroundColor: theme.palette.secondary.main,
    },
  },
}));

const StyledDialog = styled(Dialog)(({ theme }) => ({
  [`& .${classes.reviewDialog}`]: {
    minWidth: "50vw",
    maxHeight: "60vh",
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
}));

const ShopReviews = () => {
  const { shop } = useShopContext();

  const iOS = typeof navigator !== "undefined" && /iPad|iPhone|iPod/.test(navigator.userAgent);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [pageNum, setPageNum] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [fetchingTestimonials, setFetchingTestimonials] = useState(false);
  const [shopTestimonials, setShopTestimonials] = useState([]);

  const openTestimonialDrawer = () => {
    setFetchingTestimonials(true);
    setDrawerOpen(true);
    fetchShopTestimonials(shop && shop.shopfront_id, pageNum)
      .then((res) => {
        // console.log(res);
        if (res && res.data) {
          setShopTestimonials(res.data.results);

          if (res.data.next) {
            setHasMore(true);
          }
        }
      })
      .catch((err) => console.log(err))
      .finally(() =>
        setTimeout(() => {
          setFetchingTestimonials(false);
        }, 300),
      );
  };

  const resetDrawerState = () => {
    setShopTestimonials([]);
    setPageNum(1);
    setHasMore(false);
  };

  useEffect(() => {
    if (pageNum > 1) {
      fetchShopTestimonials(shop && shop.shopfront_id, pageNum)
        .then((res) => {
          // console.log(res);
          if (res && res.data) {
            let arr = [...shopTestimonials];
            arr = arr.concat(res.data.results);
            setShopTestimonials(arr);

            if (res.data.next) {
              setHasMore(true);
            } else {
              setHasMore(false);
            }
          }
        })
        .catch((err) => console.log(err));
    }
    // eslint-disable-next-line
  }, [pageNum]);

  const renderReviews = () => {
    if (fetchingTestimonials) {
      return (
        <StyledEmptyReview style={{ textAlign: "center" }}>
          <StyledCircularProgress />
          <Typography variant="h6">Fetching reviews...</Typography>
        </StyledEmptyReview>
      );
    }

    if (shopTestimonials.length <= 0) {
      return (
        <StyledEmptyReview>
          <Typography variant="subtitle">No reviews.</Typography>
        </StyledEmptyReview>
      );
    }
    return (
      <StyledReview id="scrollableDiv">
        <InfiniteScroll
          dataLength={shopTestimonials.length}
          next={() => setPageNum(pageNum + 1)}
          hasMore={hasMore}
          loader={
            <Typography variant="body1" style={{ fontWeight: 600 }}>
              Fetching reviews...
            </Typography>
          }
          scrollableTarget="scrollableDiv"
        >
          {shopTestimonials.map((testimonial, index) => {
            return (
              <div key={index} style={{ wordWrap: "break-word", marginBottom: "24px" }}>
                <Typography variant="body2" style={{ fontWeight: 500 }}>
                  {testimonial.customer_name}
                </Typography>
                <div className={classes.reviewRatingSection}>
                  <StyledRating
                    value={parseFloat(testimonial.shop_rating)}
                    precision={0.1}
                    readOnly
                    size="small"
                    emptyIcon={<StyledStarBorder fontSize="inherit" />}
                  />
                  <Typography style={{ fontWeight: 300, fontSize: 13 }}>
                    {new Date(testimonial.date_created).toLocaleDateString("en-GB")}
                  </Typography>
                </div>
                <Typography variant="body2">
                  {testimonial.shop_review ? (
                    testimonial.shop_review
                  ) : (
                    <span style={{ fontStyle: "italic" }}>~No Review Given~</span>
                  )}
                </Typography>
              </div>
            );
          })}
        </InfiniteScroll>
      </StyledReview>
    );
  };

  return (
    <Fragment>
      {shop?.display_reviews && (
        <Stack alignItems="center">
          <Stack
            direction="row"
            gap={1}
            onClick={() => openTestimonialDrawer()}
            sx={{ cursor: "pointer" }}
            alignItems="center"
          >
            <StyledRating
              value={shop.shop_testimonial_info?.shop_rating}
              precision={0.1}
              readOnly
              emptyIcon={<StyledStarBorder fontSize="inherit" />}
            />
            <StyledTypography textcolor={shop?.text_color} variant="body1" fontWeight={500}>
              {shop.shop_testimonial_info?.shop_rating?.toFixed(1)}
            </StyledTypography>
          </Stack>

          <StyledTypography textcolor={shop?.text_color} variant="body2" fontStyle="italic">
            ({shop?.shop_testimonial_info.shop_testimonials_length} reviews)
          </StyledTypography>
        </Stack>
      )}
      <Hidden mdUp>
        <StyledSwipeableDrawer
          anchor={"bottom"}
          open={drawerOpen}
          disableBackdropTransition={!iOS}
          disableDiscovery={iOS}
          onClose={() => setDrawerOpen(false)}
          onOpen={() => setDrawerOpen(true)}
          SlideProps={{ onExited: () => resetDrawerState() }}
        >
          <div className={classes.list} role="presentation">
            <div className={classes.reviewSectionHeader}>
              <div className={classes.dragHandle} />
              <Stack sx={{ mt: 2 }}>
                <Typography variant="h6">Reviews</Typography>
                <Stack direction="row" gap={0.5} alignItems="center">
                  <StyledRating
                    value={shop && shop.shop_testimonial_info.shop_rating}
                    precision={0.1}
                    readOnly
                    style={{ fontSize: "1.3rem" }}
                    emptyIcon={<StyledStarBorder fontSize="inherit" />}
                  />
                  <StyledTypography textcolor={shop?.text_color} variant="body1" fontWeight={500}>
                    {shop?.shop_testimonial_info?.shop_rating?.toFixed(1)}
                  </StyledTypography>
                  <StyledTypography textcolor={shop?.text_color} variant="body2" fontStyle="italic">
                    ({shop?.shop_testimonial_info.shop_testimonials_length})
                  </StyledTypography>
                </Stack>
              </Stack>
            </div>

            {renderReviews()}
          </div>
        </StyledSwipeableDrawer>
      </Hidden>
      <Hidden smDown>
        <StyledDialog
          classes={{ paper: classes.reviewDialog }}
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          TransitionProps={{ onExited: () => resetDrawerState() }}
        >
          <DialogTitle>
            <Typography variant="h6">Reviews</Typography>
            <Stack direction="row" gap={0.5} alignItems="center">
              <StyledRating
                value={shop && shop.shop_testimonial_info.shop_rating}
                precision={0.1}
                readOnly
                style={{ fontSize: "1.3rem" }}
                emptyIcon={<StyledStarBorder fontSize="inherit" />}
              />
              <StyledTypography textcolor={shop?.text_color} variant="body1" fontWeight={500}>
                {shop?.shop_testimonial_info?.shop_rating?.toFixed(1)}
              </StyledTypography>
              <StyledTypography textcolor={shop?.text_color} variant="body2" fontStyle="italic">
                ({shop?.shop_testimonial_info.shop_testimonials_length})
              </StyledTypography>
            </Stack>
          </DialogTitle>
          {renderReviews()}
        </StyledDialog>
      </Hidden>
    </Fragment>
  );
};

export default ShopReviews;
